// File: src/MathTools/SinusoidTool.js
//----------------------------------------------------------
// A React component that plots y = A*sin(B(x - C)) + D
// and provides sliders so the student can experiment with each parameter.

import React, { useState } from "react";
import CoordinatePlane from "./CoordinatePlane";

export default function SinusoidTool({
  initialA = 1,
  initialB = 1,
  initialC = 0,
  initialD = 0,
  width = 500,
  height = 300
}) {
  const [A, setA] = useState(initialA);
  const [B, setB] = useState(initialB);
  const [C, setC] = useState(initialC);
  const [D, setD] = useState(initialD);

  // We’ll generate line segments approximating the sine wave on x from -2π..+2π.
  // Adjust the domain if you prefer a different x-range.
  const xMin = -2 * Math.PI;
  const xMax =  2 * Math.PI;
  const stepCount = 200;
  const dx = (xMax - xMin) / stepCount;

  const lines = [];
  let minY = Infinity, maxY = -Infinity;
  
  for (let i = 0; i < stepCount; i++) {
    const x1 = xMin + i * dx;
    const x2 = x1 + dx;

    const y1 = A * Math.sin(B * (x1 - C)) + D;
    const y2 = A * Math.sin(B * (x2 - C)) + D;
    
    // track min/max for auto-scaling
    if (y1 < minY) minY = y1;
    if (y1 > maxY) maxY = y1;
    if (y2 < minY) minY = y2;
    if (y2 > maxY) maxY = y2;
    
    lines.push({
      start: [x1, y1],
      end:   [x2, y2],
      color: "blue"
    });
  }

  // A little padding for the Y-domain
  const pad = 0.2 * (maxY - minY || 1);
  const yMin = minY - pad;
  const yMax = maxY + pad;

  const period = (Math.abs(B) < 1e-9) 
    ? "undefined" 
    : (2 * Math.PI / Math.abs(B)).toFixed(2);

  const amplitude = Math.abs(A).toFixed(2);

  return (
    <div style={{ border: "1px solid #ccc", padding: 10, borderRadius: 6 }}>
      <h4>Sinusoid Explorer</h4>
      <p>
        This tool plots <code>y = A &middot; sin[B(x - C)] + D</code>. 
        Adjust sliders to see the effect on amplitude, period, shift, and midline.
      </p>

      {/* Parameter Sliders */}
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <div>
          <label>A (Amplitude): {A.toFixed(2)}</label>
          <br/>
          <input
            type="range" min="-5" max="5" step="0.1"
            value={A}
            onChange={e => setA(parseFloat(e.target.value))}
          />
        </div>
        <div>
          <label>B (controls period): {B.toFixed(2)}</label>
          <br/>
          <input
            type="range" min="-3" max="3" step="0.1"
            value={B}
            onChange={e => setB(parseFloat(e.target.value))}
          />
        </div>
        <div>
          <label>C (horizontal shift): {C.toFixed(2)}</label>
          <br/>
          <input
            type="range" min="-3" max="3" step="0.1"
            value={C}
            onChange={e => setC(parseFloat(e.target.value))}
          />
        </div>
        <div>
          <label>D (vertical shift): {D.toFixed(2)}</label>
          <br/>
          <input
            type="range" min="-3" max="3" step="0.1"
            value={D}
            onChange={e => setD(parseFloat(e.target.value))}
          />
        </div>
      </div>

      {/* The coordinate plane plotting the sine wave */}
      <CoordinatePlane
        width={width}
        height={height}
        xDomain={[xMin, xMax]}
        yDomain={[yMin, yMax]}
        lines={lines}
        showAxes
        disablePlot={true}
        style={{ marginTop: 12 }}
      />

      {/* Display some derived info */}
      <p style={{ marginTop: 8 }}>
        <strong>Amplitude</strong> = |A| = {amplitude}<br/>
        <strong>Period</strong> = 2π / |B| = {period}<br/>
        <strong>Phase shift</strong> = C<br/>
        <strong>Vertical shift (midline)</strong> = D
      </p>
    </div>
  );
}
